import React from 'react';
import {
  Star,
  CheckCircle2,
  GraduationCap,
  Newspaper,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { BadgeTooltip } from './BadgeTooltip';
import { useTranslation } from 'react-i18next';

export type BadgeType =
  | 'founding_creator'
  | 'verified_identity'
  | 'verified_expert'
  | 'verified_journalist'
  | 'official';

export type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeIconProps {
  badgeType: BadgeType;
  size?: BadgeSize;
  showTooltip?: boolean;
  className?: string;
}

const BADGE_CONFIG: Record<
  BadgeType,
  {
    icon: LucideIcon;
    colorClass: string;
    nameKey: string;
    descriptionKey: string;
  }
> = {
  founding_creator: {
    icon: Star,
    colorClass: 'text-amber-500',
    nameKey: 'badges.foundingCreator.name',
    descriptionKey: 'badges.foundingCreator.description',
  },
  verified_identity: {
    icon: CheckCircle2,
    colorClass: 'text-blue-500',
    nameKey: 'badges.verifiedIdentity.name',
    descriptionKey: 'badges.verifiedIdentity.description',
  },
  verified_expert: {
    icon: GraduationCap,
    colorClass: 'text-iris-500',
    nameKey: 'badges.verifiedExpert.name',
    descriptionKey: 'badges.verifiedExpert.description',
  },
  verified_journalist: {
    icon: Newspaper,
    colorClass: 'text-emerald-500',
    nameKey: 'badges.verifiedJournalist.name',
    descriptionKey: 'badges.verifiedJournalist.description',
  },
  official: {
    icon: ShieldCheck,
    colorClass: 'text-brand-primary',
    nameKey: 'badges.official.name',
    descriptionKey: 'badges.official.description',
  },
};

const SIZE_MAP: Record<BadgeSize, { iconSize: number; containerClass: string }> = {
  sm: { iconSize: 12, containerClass: 'w-3 h-3' },
  md: { iconSize: 16, containerClass: 'w-4 h-4' },
  lg: { iconSize: 20, containerClass: 'w-5 h-5' },
};

export function BadgeIcon({
  badgeType,
  size = 'md',
  showTooltip = true,
  className = '',
}: BadgeIconProps) {
  const { t } = useTranslation();
  const config = BADGE_CONFIG[badgeType];
  if (!config) return null;

  const { icon: Icon, colorClass, nameKey } = config;
  const name = t(nameKey);
  const { iconSize, containerClass } = SIZE_MAP[size];

  const iconElement = (
    <span
      className={`inline-flex items-center justify-center ${containerClass} ${className}`}
      role="img"
      aria-label={name}
    >
      <Icon size={iconSize} className={colorClass} />
    </span>
  );

  if (showTooltip) {
    return (
      <BadgeTooltip badgeType={badgeType}>
        {iconElement}
      </BadgeTooltip>
    );
  }

  return iconElement;
}

export function getBadgeConfig(badgeType: BadgeType) {
  return BADGE_CONFIG[badgeType];
}

export function isBadgeType(value: string): value is BadgeType {
  return value in BADGE_CONFIG;
}

export default BadgeIcon;
